import { useMemo, useState } from "react";
import { Download } from "lucide-react";
import { DsButton } from "../../../design-system";
import {
  canImportAll,
  canImportSuccessfulOnly,
} from "./importValidationTypes";

/**
 * Shared import validation review pane.
 * Success / Failed tabs with code, name, and status/reason.
 * Consent: Import all (only if zero failures) OR Ignore failed & import successful.
 */
export default function ImportValidationReview({
  result,
  busy = false,
  title = "Validation results",
  entityLabel = "record",
  onImportAll,
  onImportSuccessfulOnly,
  onDownloadReport,
  onBack,
}) {
  const [tab, setTab] = useState(
    result?.failedCount > 0 ? "failed" : "success"
  );

  const list = useMemo(() => {
    if (!result) return [];
    return tab === "failed" ? result.failedRows : result.successRows;
  }, [result, tab]);

  if (!result) return null;

  const { successCount, failedCount, totalCount } = result;
  const importAllOk = canImportAll(result);
  const importPartialOk = canImportSuccessfulOnly(result);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="ds-h4 text-[hsl(var(--ds-fg))]">{title}</p>
          <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
            {totalCount} {entityLabel}
            {totalCount === 1 ? "" : "s"} checked —{" "}
            <span className="font-semibold text-[hsl(var(--ds-success))]">
              {successCount} successful
            </span>
            {", "}
            <span
              className={`font-semibold ${
                failedCount
                  ? "text-[hsl(var(--ds-danger))]"
                  : "text-[hsl(var(--ds-muted-fg))]"
              }`}
            >
              {failedCount} failed
            </span>
          </p>
        </div>
        {onDownloadReport ? (
          <DsButton
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={onDownloadReport}
            className="gap-2"
          >
            <Download size={16} />
            Download report
          </DsButton>
        ) : null}
      </div>

      <div className="flex gap-1 border-b border-[hsl(var(--ds-border))]">
        <button
          type="button"
          className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${
            tab === "success"
              ? "border-[hsl(var(--ds-success))] text-[hsl(var(--ds-success))]"
              : "border-transparent text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
          }`}
          onClick={() => setTab("success")}
        >
          Successful ({successCount})
        </button>
        <button
          type="button"
          className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${
            tab === "failed"
              ? "border-[hsl(var(--ds-danger))] text-[hsl(var(--ds-danger))]"
              : "border-transparent text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
          }`}
          onClick={() => setTab("failed")}
        >
          Failed ({failedCount})
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-[hsl(var(--ds-border))]">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]">
            <tr>
              <th className="px-3 py-2 font-semibold w-16">Row</th>
              <th className="px-3 py-2 font-semibold w-28">Code</th>
              <th className="px-3 py-2 font-semibold w-40">Name</th>
              <th className="px-3 py-2 font-semibold">
                {tab === "failed" ? "Reason" : "Status"}
              </th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-3 py-8 text-center text-[hsl(var(--ds-muted-fg))]"
                >
                  No {tab === "failed" ? "failed" : "successful"}{" "}
                  {entityLabel}s.
                </td>
              </tr>
            ) : (
              list.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-[hsl(var(--ds-border))] align-top"
                >
                  <td className="px-3 py-2 text-[hsl(var(--ds-muted-fg))]">
                    {row.row}
                  </td>
                  <td className="px-3 py-2 font-medium text-[hsl(var(--ds-fg))]">
                    {row.code || "—"}
                  </td>
                  <td className="px-3 py-2 text-[hsl(var(--ds-fg))]">
                    {row.name || "—"}
                  </td>
                  <td
                    className={`px-3 py-2 ${
                      row.status === "failed"
                        ? "text-[hsl(var(--ds-danger))]"
                        : "text-[hsl(var(--ds-success))]"
                    }`}
                  >
                    {row.status === "failed" ? row.reason : "Success"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {failedCount > 0 ? (
        <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
          Import all is blocked until every row is valid. You can ignore failed
          rows and import only the successful ones, or fix the file and
          re-validate.
        </p>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[hsl(var(--ds-border))] pt-3">
        {onBack ? (
          <DsButton
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={onBack}
          >
            Back
          </DsButton>
        ) : null}
        {onImportSuccessfulOnly ? (
          <DsButton
            type="button"
            variant="outline"
            size="sm"
            disabled={busy || !importPartialOk || failedCount === 0}
            onClick={onImportSuccessfulOnly}
            title={
              failedCount === 0
                ? "No failed rows to ignore"
                : "Import successful rows only"
            }
          >
            {busy
              ? "Importing…"
              : `Ignore failed & import (${successCount})`}
          </DsButton>
        ) : null}
        {onImportAll ? (
          <DsButton
            type="button"
            variant="primary"
            size="sm"
            disabled={busy || !importAllOk}
            onClick={onImportAll}
            title={
              failedCount > 0
                ? "Fix failed rows or use Ignore failed & import"
                : "Import all successful rows"
            }
          >
            {busy
              ? "Importing…"
              : importAllOk
                ? `Import all (${successCount})`
                : "Import all (blocked)"}
          </DsButton>
        ) : null}
      </div>
    </div>
  );
}
